import { FC } from "react";
import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  Modal,
  Row,
  TimePicker,
  message,
} from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PlusOutlined } from "@ant-design/icons";
import { queryKeys } from "../../../services/react-query/queryKeys";
import DeleteSVG from "../../../assets/common/DeleteSVG";
import { addAdditionalShift } from "../../../network/workingHours";
import arLocale from "antd/es/date-picker/locale/ar_EG";
import enLocale from "antd/es/date-picker/locale/en_US";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { location } from "../../../types/calendar";
import dayjs from "dayjs";
import { getWeekDays } from "../../../utils/DateUtils";
import { adminAdditionalShifts } from "../../../network/admin";

interface AddAddtionalShiftProps {
  visible: boolean;
  onClose: () => void;
  team_member_id: number;
  location_id: number;
  selectedDate: any;
  config?: {
    isAdmin?: boolean;
    isEdit?: boolean;
    date?: string;
  };
}

const AddAdditionalShift: FC<AddAddtionalShiftProps> = ({
  visible,
  onClose,
  team_member_id,
  selectedDate,
  location_id,
  config,
}) => {
  const intl = useIntl();
  const { currentLang, activeLocation } = useSelector(
    (state: {
      user: { currentLang: "en" | "ar" };
      calendar: { activeLocation: location | null };
    }) => {
      return {
        currentLang: state?.user?.currentLang,
        activeLocation: state.calendar.activeLocation,
      };
    }
  );
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const { mutate: onAddAddtionalShift, isLoading } = useMutation(
    (data: object) => {
      if (config?.isAdmin) {
        return adminAdditionalShifts(location_id, team_member_id, {
          ...data,
          date: config?.date,
        });
      } else {
        return addAdditionalShift(location_id, team_member_id, data);
      }
    },
    {
      onSuccess: (res: any) => {
        if (
          res?.response?.status === 422 ||
          res?.response?.status === 500 ||
          res?.response?.status === 404
        ) {
          message.error(res?.response?.data?.message);
        } else {
          onClose();
          queryClient.invalidateQueries([
            config?.isAdmin ? "TeamMember" : queryKeys.workingHours,
          ]);
          message.success("Submit success!");
        }
      },
      onError: (error) => {
        message.error("حاول مرة اخرى");
      },
    }
  );
  const formatTime = "h:mm ";
  const onFinish = async (values: any) => {
    const data: any = {};
    data.date = selectedDate;
    // data.schedule_type = "never";

    data.periods = values.working_shifts.map((shift: any) => ({
      // ...shift,
      from: shift.start_time?.format("HH:mm"),
      to: shift.end_time?.format("HH:mm"), // Access end_time from each shift
      // shift_type: "additional",
      // day: dayName,
    }));

    onAddAddtionalShift(data);
  };
  const dayIndex = dayjs(selectedDate)?.day();
  const day = getWeekDays(new Date(selectedDate), "en")[dayIndex];
  const workingDay = activeLocation?.opening_hours?.find(
    (d) => d?.day?.toLocaleLowerCase() === day?.day?.toLocaleLowerCase()
  );

  // const selectedDateObject = new Date(selectedDate);

  return (
    <Modal
      centered
      open={visible}
      onCancel={onClose}
      okButtonProps={{ disabled: true }}
      cancelButtonProps={{ disabled: true }}
      footer={[]}
      width={740}
    >
      <Card className="mx-12 my-7 border-0">
        <div className="text-color-primary text-2xl font-semibold leading-7 text-center">
          {intl.formatMessage({
            id: "additionalShift",
            defaultMessage: "دوره عمل اضافيه",
          })}
        </div>

        <div className="my-4 text-color-gray text-base font-normal leading-7 text-center">
          {selectedDate}
        </div>

        <Form
          name="complex-form"
          onFinish={onFinish}
          layout="vertical"
          className="form-style"
          form={form}
          initialValues={{
            working_shifts: [{}],
          }}
        >
          <Form.List name="working_shifts">
            {(fields, { add, remove }) => (
              <>
                <Form.Item className="p-2 rounded w-full text-color-primary">
                  {fields.map(({ key, name, ...restField }, index) => (
                    <Row key={key} gutter={{ xs: 8, sm: 16, md: 24, lg: 15 }}>
                      <Col
                        className="gutter-row"
                        xl={10}
                        lg={12}
                        xs={24}
                        sm={12}
                      >
                        <Form.Item
                          {...restField}
                          required={false}
                          label={intl.formatMessage({
                            id: "shiftStart",
                            defaultMessage: "بدايه الدوره",
                          })}
                          name={[name, "start_time"]}
                          rules={[
                            {
                              required: true,
                              message: intl.formatMessage({
                                id: "shiftStartRequired",
                                defaultMessage: "يجب تحديد بداية الدوره",
                              }),
                            },
                          ]}
                        >
                          <TimePicker
                            locale={currentLang === "ar" ? arLocale : enLocale}
                            placeholder={intl.formatMessage({
                              id: "shiftStart",
                              defaultMessage: "بدايه الدوره",
                            })}
                            className="w-full my-input"
                            format={"hh:mm A"} // minuteStep={5}
                            needConfirm={false}
                            disabledTime={(time) => {
                              // const selectedStartTime = day.from;

                              const disabledHours = Array.from(
                                { length: 24 },
                                (_, hour) => hour
                              );

                              if (!workingDay) return {};
                              const startHour =
                                +workingDay?.opening_time?.split(":")[0];
                              // const endHour = +workingDay?.closing_time?.split(":")[0];
                              const newDisabled = disabledHours?.filter(
                                (hour) => hour < +startHour
                                // || hour > +endHour
                              );
                              // const disabledMinutes = Array.from(
                              //   { length: 60 },
                              //   (_, minute) => minute
                              // );
                              // const startMinute = +selectedStartTime.split(":")[1];
                              // disabledMinutes.splice(startMinute, 60);
                              const disabled = {
                                disabledHours: () => newDisabled,
                                // disabledMinutes: () => disabledMinutes,
                              };

                              return disabled;
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col
                        className="gutter-row"
                        xl={10}
                        lg={12}
                        xs={24}
                        sm={12}
                      >
                        <Form.Item
                          {...restField}
                          required={false}
                          name={[name, "end_time"]}
                          label={intl.formatMessage({
                            id: "shiftEnd",
                            defaultMessage: "نهايه الدوره",
                          })}
                          rules={[
                            {
                              required: true,
                              message: intl.formatMessage({
                                id: "shiftEndRequired",
                                defaultMessage: "يجب تحديد نهايه الدوره",
                              }),
                            },
                          ]}
                        >
                          <TimePicker
                            locale={currentLang === "ar" ? arLocale : enLocale}
                            placeholder={intl.formatMessage({
                              id: "shiftEnd",
                              defaultMessage: "نهايه الدوره",
                            })}
                            className="w-full my-input"
                            format={"hh:mm A"}
                            minuteStep={5}
                            disabledTime={() => {
                              const selectedStartTimes = fields.map(
                                (_, index) =>
                                  form.getFieldValue([
                                    "working_shifts",
                                    index,
                                    "start_time",
                                  ])
                              );

                              if (selectedStartTimes) {
                                const disabledHours = Array.from(
                                  { length: 24 },
                                  (_, hour) => hour
                                );
                                const startHour =
                                  selectedStartTimes[index]?.hour();
                                disabledHours.splice(startHour, 24);

                                const disabledMinutes = Array.from(
                                  { length: 60 },
                                  (_, minute) => minute
                                );
                                const startMinute =
                                  selectedStartTimes[index]?.minute();
                                disabledMinutes.splice(startMinute, 60);

                                return {
                                  disabledHours: () => disabledHours,
                                  disabledMinutes: () => disabledMinutes,
                                };
                              }
                              return {};
                            }}
                            needConfirm={false}
                          />
                        </Form.Item>
                      </Col>
                      <Col
                        className="gutter-row"
                        xl={2}
                        lg={12}
                        xs={24}
                        sm={12}
                        span={12}
                      >
                        {fields.length > 1 ? (
                          <Form.Item label=" ">
                            <Button
                              onClick={() => remove(index)}
                              className="border-none bg-transparent shadow-none outline-none"
                              block
                              icon={<DeleteSVG />}
                            ></Button>
                          </Form.Item>
                        ) : null}
                      </Col>
                    </Row>
                  ))}

                  <Flex justify="flex-end">
                    <Form.Item>
                      <Button
                        className="border-none shadow-none outline-none bg-transparent"
                        onClick={() => add()}
                        block
                        icon={
                          <PlusOutlined className="bg-[#00086D]  text-white text-sm  rounded-md p-1 " />
                        }
                      >
                        {intl.formatMessage({
                          id: "addNewShift",
                          defaultMessage: "اضافة دورة جديدة",
                        })}
                      </Button>
                    </Form.Item>
                  </Flex>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Flex gap="middle" align="center" justify="center">
            <Button type="primary" block htmlType="submit" loading={isLoading}>
              {intl.formatMessage({
                id: "save",
                defaultMessage: "حفظ",
              })}
            </Button>
            <Button
              type="primary"
              block
              className="btn-primary-light"
              onClick={onClose}
            >
              {intl.formatMessage({
                id: "cancel",
                defaultMessage: "إلغاء",
              })}
            </Button>
          </Flex>
        </Form>
      </Card>
    </Modal>
  );
};

export default AddAdditionalShift;
